import { useEffect, useRef, useState } from "react";
import { apiFetch, apiUrl } from "./api";

export type StationKind = "radio" | "podcast";
export type StreamFormat = "mp3" | "aac" | "hls";

export type Station = {
  id: string;
  name: string;
  tagline: string;
  stream: string;
  art: string | null;
  accent: string;
  kind: StationKind;
  group: string;
  format: StreamFormat;
};

export type NowPlaying = {
  artist: string | null;
  title: string | null;
  album?: string | null;
  albumartUrl?: string | null;
  startedAt?: number;
  duration?: number | null;
  /** Podcasts ship the latest episode URL here; overrides station.stream. */
  stream?: string | null;
  format?: StreamFormat | null;
  station?: { id: string; name: string };
};

export function useStations() {
  const [stations, setStations] = useState<Station[]>([]);
  const stationsRef = useRef<Station[]>([]);
  stationsRef.current = stations;

  useEffect(() => {
    let cancelled = false;
    let retry: number | null = null;

    const load = async () => {
      try {
        const r = await apiFetch("/api/stations");
        if (!r.ok) throw new Error(`stations ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        if (data.stations?.length) {
          setStations(data.stations);
          if (retry) { clearTimeout(retry); retry = null; }
          return;
        }
        throw new Error("empty");
      } catch {
        // No connection at cold boot (car modem still coming up) is the
        // common case — keep retrying so the list appears once the link
        // is up, instead of staying empty until the app is relaunched.
        if (!cancelled) retry = window.setTimeout(load, 5000);
      }
    };
    load();

    // Re-check on WebView resume if we still have no stations.
    const onVisible = () => {
      if (document.visibilityState === "visible" && !stationsRef.current.length) load();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
  return stations;
}

/**
 * useNowPlaying — live track metadata for the active station.
 *
 * Primary path is an SSE subscription to `/api/now-playing/:id/stream`:
 * the server holds one persistent ICY connection per station and reads
 * the audio stream continuously, so a track change is pushed the moment
 * the StreamTitle flips — no polling lag, no missed changes. If SSE
 * can't establish (no EventSource, hard error, blocked proxy) we fall
 * back to the old 10 s poll.
 */
export function useNowPlaying(stationId: string | null, playing: boolean) {
  const [np, setNp] = useState<NowPlaying | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    const cleanup = () => {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };

    if (!stationId || !playing) {
      setNp(null);
      cleanup();
      return;
    }

    let cancelled = false;

    // ── polling fallback ──
    const startPolling = () => {
      if (intervalRef.current) return;
      const fetchNow = async () => {
        try {
          const r = await apiFetch(`/api/now-playing/${stationId}`);
          if (!r.ok) return;
          const data = await r.json();
          if (!cancelled) setNp(data);
        } catch {}
      };
      fetchNow();
      intervalRef.current = window.setInterval(fetchNow, 10_000);
    };

    // ── primary: SSE ──
    if (typeof EventSource !== "undefined") {
      try {
        const es = new EventSource(apiUrl(`/api/now-playing/${stationId}/stream`));
        esRef.current = es;
        es.onmessage = (e) => {
          if (cancelled || !e.data) return;
          try { setNp(JSON.parse(e.data) as NowPlaying); } catch {}
        };
        es.onerror = () => {
          // EventSource auto-reconnects while CONNECTING; only give up and
          // fall back to polling once it has permanently closed.
          if (es.readyState === EventSource.CLOSED) {
            es.close();
            if (esRef.current === es) esRef.current = null;
            if (!cancelled) startPolling();
          }
        };
      } catch {
        startPolling();
      }
    } else {
      startPolling();
    }

    return () => { cancelled = true; cleanup(); };
  }, [stationId, playing]);

  return np;
}
